import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { textStyle } from "../../styles/Style";
import "../../styles/popupStyle.css";
import { phoneTypes } from "../../../constants/PhoneTypes";
import { Languages } from "../../../constants/languages";
import GooglePlacesAutocomplete, {
  Place,
} from "../../subcomponents/GoogleAutocomplete";
import {
  validateEmail,
  validatePhoneNumber,
  validationMessages,
} from "../../actions/validations";
import {
  converttoDayjsTime,
  converttoStandardTime,
  propertyTime,
} from "../../../constants/PropertyInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { reservePropertyInformationTooltipHeader } from "../../../constants/ReserveTooltipInformation";

const ReservePropertyInformation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [propertyName, setPropertyName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [addressLine1, setAddressLine1] = useState<string | null>(null);
  const [addressLine2, setAddressLine2] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [zipCode, setZipCode] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [language, setLanguage] = useState<string | null>(null);
  const [checkinTime, setCheckinTime] = useState<any>(propertyTime.CHECK_IN);
  const [checkoutTime, setCheckoutTime] = useState<any>(propertyTime.CHECK_OUT);
  const [nameEntered, setNameEntered] = useState<boolean>(false);
  const [phoneType, setPhoneType] = useState<string | null>(null);

  useEffect(() => {
    if (fieldValues && fieldValues.propertyName !== null) {
      setPropertyName(fieldValues.propertyName);
      setAddressLine1(fieldValues.addressLine1);
      setAddressLine2(fieldValues.addressLine2);
      setCity(fieldValues.city);
      setState(fieldValues.state);
      setCountry(fieldValues.country);
      setPhoneNumber(fieldValues.phoneNumber);
      setEmail(fieldValues.email);
      setZipCode(fieldValues.zipCode);
      setCheckinTime(converttoDayjsTime(fieldValues.checkinTime));
      setCheckoutTime(converttoDayjsTime(fieldValues.checkoutTime));
      if (
        fieldValues.phoneType !== undefined &&
        fieldValues.phoneType !== null
      ) {
        setPhoneType(String(fieldValues.phoneType));
      }
      if (fieldValues.language !== undefined && fieldValues.language !== null) {
        setLanguage(String(fieldValues.language));
      }
    }
  }, []);

  useEffect(() => {
    validateInput();
    data({
      propertyName,
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      zipCode,
      phoneNumber,
      email,
      phoneType,
      language,
      checkinTime: converttoStandardTime(checkinTime),
      checkoutTime: converttoStandardTime(checkoutTime),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parent passes unstable callback
  }, [
    propertyName,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    zipCode,
    phoneNumber,
    email,
    phoneType,
    language,
    checkinTime,
    checkoutTime,
  ]);

  const updateAddress = (addressComponents: any) => {
    let nextLine1 = "";
    let nextLine2 = "";
    let nextCity = "";
    let nextState = "";
    let nextCountry = "";
    let nextZip = "";

    addressComponents.forEach((component: any) => {
      const primaryType = component.types[0];

      switch (primaryType) {
        case "street_number":
          nextLine1 = component.long_name + " " + nextLine1;
          break;
        case "route":
          nextLine1 = nextLine1 + component.long_name;
          break;
        case "sublocality":
        case "sublocality_level_1":
          nextLine2 = component.long_name;
          break;
        case "neighborhood":
          nextLine2 = component.long_name;
          break;
        case "subpremise":
          nextLine2 = component.long_name;
          break;
        case "locality":
          nextCity = component.long_name;
          break;
        case "administrative_area_level_1":
          nextState = component.long_name;
          break;
        case "country":
          nextCountry = component.long_name;
          break;
        case "postal_code":
          nextZip = component.long_name;
          break;
        default:
          break;
      }
    });

    setAddressLine1(nextLine1.trim());
    if (nextLine2) setAddressLine2(nextLine2.trim());
    if (nextCity) setCity(nextCity);
    if (nextState) setState(nextState);
    if (nextCountry) setCountry(nextCountry);
    if (nextZip) setZipCode(nextZip);
  };

  const validateInput = () => {
    setNameError(false);
    setEmailError(false);
    setPhoneNumberError(false);
    if (email && !validateEmail(email)) {
      setEmailError(true);
    }
    if (phoneNumber && !validatePhoneNumber(phoneNumber)) {
      setPhoneNumberError(true);
    }
    if (propertyName === "" && nameEntered) {
      setNameError(true);
    }
  };

  useEffect(() => {
    validateInput();
  }, [propertyName]);

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Typography variant="h6" component="div" sx={textStyle}>
          Enter your property information
          <Tooltip
            title={tooltipComponent(reservePropertyInformationTooltipHeader)}
          >
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Typography>
        <Stack>
          <FormLabel component="legend">Property Name</FormLabel>
          <TextField
            id="reserve-propertyName"
            variant="outlined"
            placeholder="Property Name"
            value={propertyName ?? ""}
            error={nameError}
            helperText={nameError ? validationMessages.propertyNameLimit : ""}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPropertyName(e.target.value);
              setNameEntered(true);
            }}
          />
        </Stack>

        <FormLabel component="legend">Property Address</FormLabel>
        <GooglePlacesAutocomplete
          apiKey={process.env.REACT_APP_GOOGLE_API_KEY ?? ""}
          onPlaceSelected={(place: Place) => {
            updateAddress(place.addressComponents);
          }}
        />
        <TextField
          id="reserve-addressLine1"
          variant="outlined"
          placeholder="Address Line 1"
          value={addressLine1 ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setAddressLine1(e.target.value);
          }}
        />
        <TextField
          id="reserve-addressLine2"
          variant="outlined"
          placeholder="Address Line 2"
          value={addressLine2 ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setAddressLine2(e.target.value);
          }}
        />
        <TextField
          id="reserve-city"
          variant="outlined"
          placeholder="City"
          value={city ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCity(e.target.value);
          }}
        />
        <TextField
          id="reserve-state"
          variant="outlined"
          placeholder="State"
          value={state ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setState(e.target.value);
          }}
        />
        <TextField
          id="reserve-country"
          variant="outlined"
          placeholder="Country"
          value={country ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setCountry(e.target.value);
          }}
        />
        <TextField
          id="reserve-zipcode"
          variant="outlined"
          placeholder="Zipcode"
          value={zipCode ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setZipCode(e.target.value);
          }}
        />

        <Stack>
          <FormLabel component="legend">Phone Type</FormLabel>{" "}
          <Select
            id="reserve-phoneType"
            value={phoneType ?? ""}
            onChange={(e) => setPhoneType(e.target.value as string)}
            sx={{ width: "100%" }}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select phone type</em>
            </MenuItem>
            {phoneTypes.map((type, index) => (
              <MenuItem value={type} key={index}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack>
          <FormLabel component="legend">Phone Number</FormLabel>
          <TextField
            id="reserve-phoneNumber"
            variant="outlined"
            placeholder="Phone Number"
            value={phoneNumber ?? ""}
            type="number"
            error={phoneNumberError}
            helperText={phoneNumberError ? validationMessages.phoneNumber : ""}
            aria-valuemax={10}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const v = e.target.value;
              setPhoneNumber(v !== "" && Number(v) >= 0 ? v : null);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">E-Mail</FormLabel>
          <TextField
            id="reserve-email"
            variant="outlined"
            placeholder="E-Mail"
            value={email ?? ""}
            error={emailError}
            helperText={emailError ? validationMessages.email : ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
            }}
          />
        </Stack>
        <FormLabel component="legend">Language</FormLabel>
        <Select
          id="reserve-language"
          value={language ?? ""}
          onChange={(e) => setLanguage(e.target.value as string)}
          sx={{ width: "100%" }}
          displayEmpty
        >
          <MenuItem value="">
            <em>Select language</em>
          </MenuItem>
          {Languages.map((type, index) => (
            <MenuItem value={type} key={index}>
              {type}
            </MenuItem>
          ))}
        </Select>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Check-in Time"
            value={checkinTime}
            onChange={(newValue) => setCheckinTime(newValue)}
          />
          <TimePicker
            label="Check-out Time"
            value={checkoutTime}
            onChange={(newValue) => setCheckoutTime(newValue)}
          />
        </LocalizationProvider>
      </Paper>
    </Stack>
  );
};

export default ReservePropertyInformation;
